/**
 * Settings Page
 *
 * Hub page for platform settings with navigation cards to sub-sections.
 */
import { motion } from 'framer-motion';
import { Settings, Users, Flag, Mail, Shield, Scale, FileText, Tags, Phone } from 'lucide-react';
import {
  SettingsNavigationCard,
  SettingsNavigationGrid,
} from '../../components/settings';

// ============================================
// Animation Variants
// ============================================

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3 } },
};

// ============================================
// Navigation Items
// ============================================

const SETTINGS_SECTIONS = [
  {
    icon: Users,
    title: 'Admin Users',
    description: 'Manage super admin users, roles, and permissions',
    to: '/settings/users',
    iconColor: 'text-violet-600 dark:text-violet-400',
    iconBgColor: 'bg-violet-100 dark:bg-violet-900/40',
  },
  {
    icon: Flag,
    title: 'Feature Flags',
    description: 'Control feature rollout with flags and targeting',
    to: '/settings/feature-flags',
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBgColor: 'bg-blue-100 dark:bg-blue-900/40',
  },
  {
    icon: FileText,
    title: 'SMS Delivery Templates',
    description: 'Manage SMS templates for document and form sending with dynamic variables',
    to: '/settings/sms-templates',
    iconColor: 'text-teal-600 dark:text-teal-400',
    iconBgColor: 'bg-teal-100 dark:bg-teal-900/40',
  },
  {
    icon: Phone,
    title: 'SMS Sender Numbers',
    description: 'Manage the shared pool of outbound SMS sender phone numbers — even distribution with per-contact stickiness',
    to: '/settings/sms-senders',
    iconColor: 'text-emerald-600 dark:text-emerald-400',
    iconBgColor: 'bg-emerald-100 dark:bg-emerald-900/40',
  },
  {
    icon: Mail,
    title: 'Email Templates',
    description: 'Customize transactional email templates with a live HTML editor — changes take effect immediately',
    to: '/settings/email-templates',
    iconColor: 'text-blue-600 dark:text-blue-400',
    iconBgColor: 'bg-blue-100 dark:bg-blue-900/40',
  },
  {
    icon: Tags,
    title: 'Template Categories',
    description: 'Manage categories for document and form templates — create, reorder, and toggle visibility',
    to: '/templates/categories',
    iconColor: 'text-violet-600 dark:text-violet-400',
    iconBgColor: 'bg-violet-100 dark:bg-violet-900/40',
  },
  {
    icon: Shield,
    title: 'Compliance Settings',
    description: 'Configure data retention, privacy, and security policies',
    to: '/settings/compliance',
    iconColor: 'text-red-600 dark:text-red-400',
    iconBgColor: 'bg-red-100 dark:bg-red-900/40',
  },
  {
    icon: Scale,
    title: 'Policy Routing Engine',
    description: 'Configure compliance rules, calling hours, DNC registries, and AI classification',
    to: '/settings/policy',
    iconColor: 'text-indigo-600 dark:text-indigo-400',
    iconBgColor: 'bg-indigo-100 dark:bg-indigo-900/40',
  },
];

// ============================================
// Component
// ============================================

export function SettingsPage() {
  return (
    <motion.div
      variants={containerVariants}
      initial="hidden"
      animate="visible"
      className="flex flex-col h-full"
    >
      {/* Header */}
      <motion.div
        variants={itemVariants}
        className="flex-shrink-0 px-6 py-6 border-b border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-700 flex items-center justify-center">
            <Settings className="w-6 h-6 text-slate-600 dark:text-slate-300" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
              Settings
            </h1>
            <p className="text-slate-500 dark:text-slate-400">
              Platform configuration and admin management
            </p>
          </div>
        </div>
      </motion.div>

      {/* Content */}
      <div className="flex-1 overflow-auto p-6">
        <motion.div variants={itemVariants}>
          <SettingsNavigationGrid>
            {SETTINGS_SECTIONS.map((section, index) => (
              <motion.div
                key={section.to}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
              >
                <SettingsNavigationCard
                  icon={section.icon}
                  title={section.title}
                  description={section.description}
                  to={section.to}
                  iconColor={section.iconColor}
                  iconBgColor={section.iconBgColor}
                />
              </motion.div>
            ))}
          </SettingsNavigationGrid>
        </motion.div>
      </div>
    </motion.div>
  );
}
